import Phaser from 'phaser';
import { getCharacter } from '@/data/personajes';
import {
  AXELIKI_FRASE_INICIAL,
  AXELIKI_FRASE_LLEGA,
  AXELIKI_FRASE_LLEGA_REBAJADO,
  AXELIKI_FRASES_ESPERA,
  AXELIKI_FRASES_INSISTIR,
  AMIGO_CIGARRO_FRASES_PIDE,
  BARRIO_ACTIONS,
  BARRIO_DESCRIPCION,
  BARRIO_TITULO,
  COMIDA_CANTIDAD,
  COMIDA_PRECIO,
  DIALOGOS_PAKI,
  DORMIR_SALUD,
  EVENTO_CIGARRO_COSTE,
  EVENTO_CIGARRO_PROB,
  FRASE_AXELIKI_IRSE,
  FRASE_DAR_CIGARRO,
  FRASES_DORMIR,
  FRASES_ESQUIVE,
  FRASES_PAKI_OK,
  FRASES_PAKIO_SIN_DINERO,
  FRASES_SIGUE_MAS,
  FRASES_TRABAJAR,
  FRASE_TRABAJAR_LIMITE,
  PROMO_FRASES_PIDE,
  TRABAJAR_GANANCIA,
  TRABAJAR_MAX_POR_VISITA,
  YERBA_CANTIDAD,
  YERBA_PRECIO,
  YERBA_PRECIO_REBAJADO,
  perseguidorAleatorio,
  pickDe,
  roll,
} from '@/data/barrio';
import { GameState } from '@/engine/GameState';
import { drawPortrait } from '@/engine/portrait';

/**
 * Paleta coherente con InteriorCasaScene / HubScene: paneles oscuros,
 * botones en gris, acentos cálidos para los botones destacados y farolas.
 */
const COLOR_FONDO_CIELO = 0x1a1a2e; // azul noche, alto
const COLOR_FONDO_SUELO = 0x12121c; // suelo más oscuro
const COLOR_FONDO_PERSIANA = 0x20202c;
const COLOR_SUPER_PARED = 0x2a3a2e; // verde-azulado de tienda
const COLOR_SUPER_LUZ = 0xb6f0d4; // fluorescente verdoso frío
const COLOR_FAROLA_POSTE = 0x33333f;
const COLOR_FAROLA_LUZ = 0xf1c40f; // amarillo cálido
const COLOR_HALO_FAROLA = 0xf1c40f; // halo amarillo translúcido
const COLOR_PATINETE = 0x00bcd4; // cian (EL PROMO)
const COLOR_BLOQUE = 0x252535;
const COLOR_BORDE_BLOQUE = 0x353545;

const COLOR_PANEL = 0x22222e;
const COLOR_PANEL_FRASE = 0x2a2a38;
const COLOR_PANEL_EVENTO = 0x3a2218; // cálido para el evento cigarro
const COLOR_BOTON = 0x2a2a38;
const COLOR_BOTON_HOVER = 0x38384a;
const COLOR_BOTON_DESTACADO = 0xc0392b;
const COLOR_BOTON_DESTACADO_HOVER = 0xe74c3c;
const COLOR_BOTON_CIGARRO = 0xc0392b; // mismo rojo para "dar cigarro"
const COLOR_BORDE_PANEL = 0x38384a;
const COLOR_TEXTO = '#ffffff';
const COLOR_TEXTO_DIM = '#8a8a95';
const COLOR_TEXTO_DESTACADO = '#fff8e1';

const MARGEN = 24;

// ---------------------------------------------------------------------
// Modos del panel central — unión discriminada
// ---------------------------------------------------------------------

type PanelMode =
  | { tipo: 'idle' }
  | { tipo: 'frase'; titulo: string; emoji: string; texto: string; variante: 'ok' | 'neutro' }
  | { tipo: 'paki'; lineaPaki: string; texto: string; ok: boolean }
  | { tipo: 'axeliki-inicial' }
  | { tipo: 'axeliki-espera'; lineaAxeliki: string }
  | { tipo: 'axeliki-compra'; precio: number; rebajado: boolean };

/** Estado de la secuencia del AXELIKI — su impuntualidad es la mecánica. */
interface AxelikiState {
  /** Ronda de espera actual (1 = primera vez que pulsas ESPERAR). 0 = aún no esperado. */
  rondaActual: number;
  /** Total de rondas que el jugador debe esperar antes de poder IRSE con pena. */
  maxRondas: number;
  /** Ronda en la que "llega el BMW" (entre 1 y maxRondas). El resto son esperas tontas. */
  llegadaEnRonda: number;
}

/**
 * Escena del BARRIO (ver `docs/biblia/escenarios.md`). Calle de
 * tarde-noche dibujada por código; una botonera principal con 4 acciones
 * (Trabajar / Paki / Axeliki / Dormir) + botón volver al HUB. Tras cada
 * acción puede aparecer, con probabilidad `EVENTO_CIGARRO_PROB`, el
 * PROMO o el AMIGO CIGARRO pidiendo un cigarro. Toda la fraseología y
 * los valores de balance vienen de `src/data/barrio.ts`.
 */
export class BarrioScene extends Phaser.Scene {
  /** Contenedor del ambiente: se dibuja UNA vez y NO se limpia al re-renderizar el UI. */
  private ambienteContainer!: Phaser.GameObjects.Container;
  /** Contenedor re-renderizable (panel central + botonera + volver). */
  private uiContainer!: Phaser.GameObjects.Container;

  private panelMode: PanelMode = { tipo: 'idle' };

  /** Contador local de trabajos en esta visita a la escena. Se resetea en `create`. */
  private trabajosEnVisita = 0;

  /** Estado de la secuencia del AXELIKI. `null` = no estamos en la secuencia. */
  private axelikiState: AxelikiState | null = null;

  /** Flag: si estamos mostrando el evento del cigarro, lo marcamos para
   *  no apilarlo tras la propia decisión. */
  private enEventoCigarro = false;

  /** Frase del PROMO/AMIGO CIGARRO en el evento actual. */
  private eventoFraseActual = '';

  /** Quién te ha pillado esta vez (PROMO o AMIGO CIGARRO). */
  private eventoPerseguidorId: 'el-promo' | 'el-amigo-cigarro' = 'el-promo';

  /** Si true, el siguiente cierre del panel del evento cigarro debe
   *  inyectar la frase de "te sigue una ronda más". */
  private eventoTeSigueMas = false;

  constructor() {
    super('BarrioScene');
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO_SUELO);

    // HUD reactivo (mismo patrón que el resto de escenas).
    const HudCtor = this.game.scene.getScene('HudScene')?.constructor
      ?? this.scene.get('HudScene')?.constructor;
    if (!this.scene.isActive('HudScene')) {
      this.scene.launch('HudScene');
    }
    this.scene.bringToTop('HudScene');
    void HudCtor;

    this.ambienteContainer = this.add.container(0, 0);
    this.uiContainer = this.add.container(0, 0);

    this.dibujarAmbiente(width, height);
    this.renderUI();
  }

  // ---------------------------------------------------------------------
  // Ambiente — calle de tarde-noche dibujada por código
  // ---------------------------------------------------------------------

  private dibujarAmbiente(width: number, height: number): void {
    // Cielo: degradado simulado con tres bandas (alto->bajo más oscuro).
    const cielo = this.add.graphics();
    cielo.fillStyle(COLOR_FONDO_CIELO, 1);
    cielo.fillRect(0, 0, width, height * 0.62);
    this.ambienteContainer.add(cielo);

    // Suelo: gris asfalto.
    const suelo = this.add.graphics();
    suelo.fillStyle(COLOR_FONDO_SUELO, 1);
    suelo.fillRect(0, height * 0.62, width, height * 0.38);
    this.ambienteContainer.add(suelo);

    // Bloques bajos a izquierda y derecha (siluetas de casas).
    this.dibujarBloque(width * 0.12, height * 0.42, width * 0.32, height * 0.2, 0.18);
    this.dibujarBloque(width * 0.56, height * 0.4, width * 0.36, height * 0.22, 0.22);

    // Súper de barrio (al fondo, lado derecho) con su luz fría.
    this.dibujarSuper(width * 0.78, height * 0.42, width * 0.22, height * 0.2);

    // Persiana a medio bajar (lado izquierdo).
    this.dibujarPersiana(width * 0.06, height * 0.55, width * 0.22, height * 0.07);

    // Farolas repartidas.
    this.dibujarFarola(width * 0.22, height * 0.62, 0.18);
    this.dibujarFarola(width * 0.5, height * 0.62, 0.2);
    this.dibujarFarola(width * 0.78, height * 0.62, 0.16);

    // Sombra lejana de un patinete al fondo (guiño a EL PROMO/AMIGO CIGARRO).
    this.dibujarPatinete(width * 0.42, height * 0.56);
  }

  private dibujarBloque(
    cx: number,
    cy: number,
    ancho: number,
    alto: number,
    opacidadVentana: number,
  ): void {
    const bloque = this.add.graphics();
    bloque.fillStyle(COLOR_BLOQUE, 1);
    bloque.fillRect(cx - ancho / 2, cy - alto / 2, ancho, alto);
    bloque.lineStyle(2, COLOR_BORDE_BLOQUE, 1);
    bloque.strokeRect(cx - ancho / 2, cy - alto / 2, ancho, alto);
    this.ambienteContainer.add(bloque);

    // Ventanas tenues con luz cálida dentro.
    const cols = 4;
    const filas = 2;
    const padX = 8;
    const padY = 8;
    const w = (ancho - padX * (cols + 1)) / cols;
    const h = (alto - padY * (filas + 1)) / filas;
    for (let f = 0; f < filas; f++) {
      for (let c = 0; c < cols; c++) {
        const x = cx - ancho / 2 + padX + c * (w + padX);
        const y = cy - alto / 2 + padY + f * (h + padY);
        bloque.fillStyle(0xf1c40f, opacidadVentana);
        bloque.fillRect(x, y, w, h);
      }
    }
  }

  private dibujarSuper(cx: number, cy: number, ancho: number, alto: number): void {
    // Tienda: pared verde-azulada.
    const superBox = this.add.graphics();
    superBox.fillStyle(COLOR_SUPER_PARED, 1);
    superBox.fillRect(cx - ancho / 2, cy - alto / 2, ancho, alto);
    superBox.lineStyle(2, 0x1e2a20, 1);
    superBox.strokeRect(cx - ancho / 2, cy - alto / 2, ancho, alto);
    this.ambienteContainer.add(superBox);

    // Toldo verde con el emoji del paki.
    const toldo = this.add.graphics();
    toldo.fillStyle(0x558b2f, 1);
    toldo.fillRect(cx - ancho / 2, cy - alto / 2 - 14, ancho, 14);
    this.ambienteContainer.add(toldo);
    this.ambienteContainer.add(
      this.add
        .text(cx, cy - alto / 2 - 7, '🏪 PAKI', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          fontStyle: 'bold',
          color: '#ffffff',
        })
        .setOrigin(0.5),
    );

    // Vitrina con luz fluorescente.
    const vitrina = this.add.graphics();
    vitrina.fillStyle(COLOR_SUPER_LUZ, 0.95);
    vitrina.fillRect(
      cx - ancho / 2 + 6,
      cy - alto / 2 + 18,
      ancho - 12,
      alto - 24,
    );
    this.ambienteContainer.add(vitrina);

    // Halo frío alrededor (luz de fluorescente).
    const halo = this.add.graphics();
    halo.fillStyle(COLOR_SUPER_LUZ, 0.08);
    halo.fillCircle(cx, cy, ancho * 0.9);
    this.ambienteContainer.add(halo);
  }

  private dibujarPersiana(cx: number, cy: number, ancho: number, alto: number): void {
    const persiana = this.add.graphics();
    persiana.fillStyle(COLOR_FONDO_PERSIANA, 1);
    persiana.fillRect(cx, cy, ancho, alto);
    this.ambienteContainer.add(persiana);

    // Rayas horizontales (lamas de la persiana).
    persiana.lineStyle(1, 0x33333f, 1);
    for (let i = 1; i < 4; i++) {
      persiana.lineBetween(cx, cy + (alto / 4) * i, cx + ancho, cy + (alto / 4) * i);
    }
    // Tira de luz inferior (escaparate entre lamas).
    persiana.fillStyle(COLOR_FAROLA_LUZ, 0.5);
    persiana.fillRect(cx, cy + alto - 5, ancho, 3);
  }

  private dibujarFarola(cx: number, cy: number, haloRadio: number): void {
    // Poste fino.
    const poste = this.add.graphics();
    poste.fillStyle(COLOR_FAROLA_POSTE, 1);
    poste.fillRect(cx - 2, cy, 4, 80);
    poste.fillStyle(COLOR_FAROLA_POSTE, 1);
    poste.fillRect(cx - 12, cy - 4, 24, 6); // brazo
    this.ambienteContainer.add(poste);

    // Bombilla.
    const bombilla = this.add.graphics();
    bombilla.fillStyle(COLOR_FAROLA_LUZ, 1);
    bombilla.fillCircle(cx, cy - 2, 5);
    this.ambienteContainer.add(bombilla);

    // Halo cálido translúcido.
    const halo = this.add.graphics();
    halo.fillStyle(COLOR_HALO_FAROLA, 0.12);
    halo.fillCircle(cx, cy + 10, haloRadio * 100);
    this.ambienteContainer.add(halo);
  }

  private dibujarPatinete(cx: number, cy: number): void {
    const patinete = this.add.graphics();
    patinete.fillStyle(COLOR_PATINETE, 0.85);
    patinete.fillRect(cx - 18, cy, 36, 3);
    patinete.fillStyle(0x22222e, 1);
    patinete.fillCircle(cx - 14, cy + 6, 4);
    patinete.fillCircle(cx + 14, cy + 6, 4);
    patinete.fillStyle(COLOR_PATINETE, 0.85);
    patinete.fillRect(cx - 1, cy - 18, 2, 18); // mástil
    patinete.fillRect(cx - 6, cy - 22, 12, 5); // manillar
    this.ambienteContainer.add(patinete);
  }

  // ---------------------------------------------------------------------
  // UI re-renderizable: panel central + botonera + volver
  // ---------------------------------------------------------------------

  private renderUI(): void {
    this.limpiarUI();

    switch (this.panelMode.tipo) {
      case 'idle':
        this.renderPanelIdle();
        break;
      case 'frase':
        this.renderPanelFrase(
          this.panelMode.titulo,
          this.panelMode.emoji,
          this.panelMode.texto,
          this.panelMode.variante,
        );
        break;
      case 'paki':
        this.renderPanelPaki(this.panelMode);
        break;
      case 'axeliki-inicial':
        this.renderPanelAxelikiInicial();
        break;
      case 'axeliki-espera':
        this.renderPanelAxelikiEspera(this.panelMode.lineaAxeliki);
        break;
      case 'axeliki-compra':
        this.renderPanelAxelikiCompra(this.panelMode.precio, this.panelMode.rebajado);
        break;
    }

    this.renderBotoneraAcciones();
    this.renderBotonVolver();
  }

  private limpiarUI(): void {
    if (!this.uiContainer) return;
    this.uiContainer.removeAll(true);
  }

  // --- Panel central: idle (descripción) -------------------------------

  private renderPanelIdle(): void {
    const { width, height } = this.scale;
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 150;
    const cy = height * 0.3;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 0.94);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.setPosition(width / 2, cy);
    this.uiContainer.add(panel);

    this.uiContainer.add(
      this.add
        .text(width / 2, cy - 36, BARRIO_TITULO, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: COLOR_TEXTO_DESTACADO,
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add
        .text(width / 2, cy + 6, BARRIO_DESCRIPCION, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '16px',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: panelAncho - 40 },
        })
        .setOrigin(0.5),
    );
  }

  // --- Panel central: frase (resultado de una acción) ----------------

  private renderPanelFrase(
    titulo: string,
    emoji: string,
    texto: string,
    variante: 'ok' | 'neutro',
  ): void {
    const { width, height } = this.scale;
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 200;
    const cy = height * 0.3;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL_FRASE, 0.96);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.setPosition(width / 2, cy);
    this.uiContainer.add(panel);

    // Emoji + título arriba.
    this.uiContainer.add(
      this.add
        .text(width / 2 - panelAncho / 2 + 50, cy - panelAlto / 2 + 38, emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '30px',
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add
        .text(width / 2 - panelAncho / 2 + 110, cy - panelAlto / 2 + 38, titulo, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          fontStyle: 'bold',
          color: COLOR_TEXTO_DESTACADO,
        })
        .setOrigin(0, 0.5),
    );

    // Texto de la frase centrada.
    this.uiContainer.add(
      this.add
        .text(width / 2, cy + 18, texto, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: panelAncho - 40 },
        })
        .setOrigin(0.5),
    );

    // Indicador ▼ abajo a la derecha.
    this.uiContainer.add(
      this.add
        .text(width / 2 + panelAncho / 2 - 26, cy + panelAlto / 2 - 22, '▼', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5),
    );

    // Texto "toca para volver" abajo centro.
    this.uiContainer.add(
      this.add
        .text(width / 2, cy + panelAlto / 2 - 22, 'toca para volver', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5),
    );

    // Tap en cualquier sitio cierra y vuelve a idle (sin nuevo evento).
    const zonaTap = this.add
      .zone(width / 2, cy, panelAncho, panelAlto)
      .setInteractive({ useHandCursor: true });
    zonaTap.on('pointerup', () => {
      this.panelMode = { tipo: 'idle' };
      this.renderUI();
    });
    this.uiContainer.add(zonaTap);

    void variante; // marcador para futuro estilo; hoy todas son iguales
  }

  // --- Panel central: paki (compra de comida con diálogo) ------------

  private renderPanelPaki(panel: Extract<PanelMode, { tipo: 'paki' }>): void {
    const { width, height } = this.scale;
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 240;
    const cy = height * 0.3;

    const fondo = this.add.graphics();
    fondo.fillStyle(COLOR_PANEL_FRASE, 0.96);
    fondo.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    fondo.lineStyle(2, COLOR_BORDE_PANEL, 1);
    fondo.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    fondo.setPosition(width / 2, cy);
    this.uiContainer.add(fondo);

    // Retrato del EL DEL PAKI a la izquierda + apodo + texto.
    const retratoX = width / 2 - panelAncho / 2 + 60;
    const retratoY = cy + 10;
    const retrato = drawPortrait(this, retratoX, retratoY, 'el-del-paki', undefined, {
      mostrarApodo: false,
      radio: 32,
      tamanoEmoji: '30px',
    });
    this.uiContainer.add(retrato);

    const char = getCharacter('el-del-paki');
    const colorHex = '#' + (char.color ?? 0x4a4a55).toString(16).padStart(6, '0');

    const textoX = width / 2 - panelAncho / 2 + 110;
    const textoAncho = panelAncho - 110 - 30;

    this.uiContainer.add(
      this.add.text(textoX, cy - 14, char.apodo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        fontStyle: 'bold',
        color: colorHex,
      }),
    );
    this.uiContainer.add(
      this.add.text(textoX, cy + 12, panel.lineaPaki, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: COLOR_TEXTO,
        wordWrap: { width: textoAncho },
      }),
    );

    // Resultado de la compra abajo, claramente.
    const estado = this.add
      .text(width / 2, cy + panelAlto / 2 - 38, panel.texto, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        fontStyle: 'bold',
        color: panel.ok ? COLOR_TEXTO_DESTACADO : COLOR_TEXTO_DIM,
        align: 'center',
        wordWrap: { width: panelAncho - 40 },
      })
      .setOrigin(0.5);
    this.uiContainer.add(estado);

    // Tap = volver a idle (luego el orquestador decidirá si evento cigarro).
    const zonaTap = this.add
      .zone(width / 2, cy, panelAncho, panelAlto)
      .setInteractive({ useHandCursor: true });
    zonaTap.on('pointerup', () => {
      this.panelMode = { tipo: 'idle' };
      this.renderUI();
    });
    this.uiContainer.add(zonaTap);
  }

  // --- Panel central: AXELIKI (inicial + espera + compra) ------------

  private renderPanelAxelikiInicial(): void {
    const { width } = this.scale;
    const cy = this.cyPanel();

    this.dibujarPanelConPersonaje(
      cy,
      width - MARGEN * 2,
      260,
      'axeliki',
      AXELIKI_FRASE_INICIAL,
      '“5 minutos y estoy” — y ya sabes cómo va esto.',
    );

    // Botones: ESPERAR / INSISTIR / IRSE.
    this.crearBotonDecisionTriple(
      width / 2,
      cy + 130,
      [
        { texto: 'ESPERAR', color: COLOR_BOTON, onClick: () => this.axelikiEsperar() },
        { texto: 'INSISTIR', color: COLOR_BOTON_DESTACADO, onClick: () => this.axelikiInsistir() },
        { texto: 'IRSE', color: COLOR_BOTON, onClick: () => this.axelikiIrse() },
      ],
      width - MARGEN * 2,
    );
  }

  private renderPanelAxelikiEspera(lineaAxeliki: string): void {
    const { width } = this.scale;
    const cy = this.cyPanel();

    const axelikiState = this.axelikiState!;
    const textoExtra = `Ronda ${axelikiState.rondaActual} de ${axelikiState.maxRondas}.`;

    this.dibujarPanelConPersonaje(
      cy,
      width - MARGEN * 2,
      260,
      'axeliki',
      lineaAxeliki,
      textoExtra,
    );

    this.crearBotonDecisionTriple(
      width / 2,
      cy + 130,
      [
        {
          texto: 'SEGUIR ESPERANDO',
          color: COLOR_BOTON,
          onClick: () => this.axelikiEsperar(),
        },
        {
          texto: 'INSISTIR',
          color: COLOR_BOTON_DESTACADO,
          onClick: () => this.axelikiInsistir(),
        },
        { texto: 'IRSE', color: COLOR_BOTON, onClick: () => this.axelikiIrse() },
      ],
      width - MARGEN * 2,
    );
  }

  private renderPanelAxelikiCompra(precio: number, rebajado: boolean): void {
    const { width } = this.scale;
    const cy = this.cyPanel();
    const tieneDinero = GameState.dinero >= precio;

    this.dibujarPanelConPersonaje(
      cy,
      width - MARGEN * 2,
      260,
      'axeliki',
      rebajado ? AXELIKI_FRASE_LLEGA_REBAJADO : AXELIKI_FRASE_LLEGA,
      rebajado
        ? `Precio especial: ${precio}€. Llegaste en el momento justo.`
        : `Precio: ${precio}€. Una más de yerba.`,
    );

    // Botón COMPRAR (verde si hay dinero, rojo apagado si no).
    const ancho = width - MARGEN * 2 - 60;
    const alto = 60;
    const y = cy + 140;
    const colorBoton = tieneDinero ? 0x2e7d32 : 0x555560;
    const boton = this.add.graphics();
    const dibujar = (c: number) => {
      boton.clear();
      boton.fillStyle(c, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(colorBoton);
    boton.setPosition(width / 2, y);
    this.uiContainer.add(boton);
    this.uiContainer.add(
      this.add
        .text(
          width / 2,
          y,
          tieneDinero ? `COMPRAR YERBA · ${precio}€` : `NO LLEGAS A ${precio}€`,
          {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '18px',
            fontStyle: 'bold',
            color: COLOR_TEXTO,
          },
        )
        .setOrigin(0.5),
    );
    const zona = this.add
      .zone(width / 2, y, ancho, alto)
      .setInteractive({ useHandCursor: tieneDinero });
    zona.on('pointerover', () => tieneDinero && dibujar(0x43a047));
    zona.on('pointerout', () => dibujar(colorBoton));
    zona.on('pointerup', () => {
      if (tieneDinero) this.axelikiComprar(precio);
    });
    this.uiContainer.add(zona);

    // Botón NO COMPRAR.
    const y2 = y + alto + 12;
    const botonNo = this.add.graphics();
    const dibujarNo = (c: number) => {
      botonNo.clear();
      botonNo.fillStyle(c, 1);
      botonNo.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujarNo(COLOR_BOTON);
    botonNo.setPosition(width / 2, y2);
    this.uiContainer.add(botonNo);
    this.uiContainer.add(
      this.add
        .text(width / 2, y2, 'NO COMPRAR', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5),
    );
    const zonaNo = this.add.zone(width / 2, y2, ancho, alto).setInteractive({ useHandCursor: true });
    zonaNo.on('pointerover', () => dibujarNo(COLOR_BOTON_HOVER));
    zonaNo.on('pointerout', () => dibujarNo(COLOR_BOTON));
    zonaNo.on('pointerup', () => this.axelikiNoComprar());
    this.uiContainer.add(zonaNo);
  }

  private cyPanel(): number {
    // Centro vertical del panel — varía con el alto del panel.
    return this.scale.height * 0.32;
  }

  /** Panel reutilizable: retrato + apodo + frase + subtítulo (sin botones). */
  private dibujarPanelConPersonaje(
    cy: number,
    panelAncho: number,
    panelAlto: number,
    personajeId: string,
    frase: string,
    subtitulo: string,
  ): void {
    const { width } = this.scale;

    const fondo = this.add.graphics();
    fondo.fillStyle(COLOR_PANEL_FRASE, 0.97);
    fondo.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    fondo.lineStyle(2, COLOR_BORDE_PANEL, 1);
    fondo.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    fondo.setPosition(width / 2, cy);
    this.uiContainer.add(fondo);

    // Retrato a la izquierda.
    const retratoX = width / 2 - panelAncho / 2 + 60;
    const retratoY = cy + 10;
    const retrato = drawPortrait(this, retratoX, retratoY, personajeId, undefined, {
      mostrarApodo: false,
      radio: 32,
      tamanoEmoji: '30px',
    });
    this.uiContainer.add(retrato);

    // Apodo en color del personaje.
    const char = getCharacter(personajeId);
    const colorHex = '#' + (char.color ?? 0x4a4a55).toString(16).padStart(6, '0');
    this.uiContainer.add(
      this.add.text(width / 2 - panelAncho / 2 + 110, cy - 30, char.apodo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: colorHex,
      }),
    );

    // Frase centrada a la derecha del retrato.
    this.uiContainer.add(
      this.add.text(
        width / 2 - panelAncho / 2 + 110,
        cy + 4,
        frase,
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: COLOR_TEXTO,
          wordWrap: { width: panelAncho - 130 - 30 },
        },
      ),
    );

    // Subtítulo contextual abajo en cursiva simulada.
    this.uiContainer.add(
      this.add
        .text(width / 2, cy + panelAlto / 2 - 30, subtitulo, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: COLOR_TEXTO_DIM,
          fontStyle: 'italic',
          align: 'center',
          wordWrap: { width: panelAncho - 40 },
        })
        .setOrigin(0.5),
    );
  }

  /** Botonera horizontal de 3 decisiones (mismo estilo, colores opcionales). */
  private crearBotonDecisionTriple(
    cx: number,
    cy: number,
    botones: { texto: string; color: number; onClick: () => void }[],
    anchoDisponible: number,
  ): void {
    const gap = 8;
    const ancho = (anchoDisponible - gap * (botones.length - 1) - 60) / botones.length;
    const alto = 56;

    botones.forEach((b, i) => {
      const x = cx - anchoDisponible / 2 + 30 + ancho / 2 + i * (ancho + gap);
      const boton = this.add.graphics();
      const dibujar = (color: number) => {
        boton.clear();
        boton.fillStyle(color, 1);
        boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 12);
      };
      dibujar(b.color);
      boton.setPosition(x, cy);
      this.uiContainer.add(boton);

      this.uiContainer.add(
        this.add
          .text(x, cy, b.texto, {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '16px',
            fontStyle: 'bold',
            color: COLOR_TEXTO,
            align: 'center',
            wordWrap: { width: ancho - 12 },
          })
          .setOrigin(0.5),
      );

      const zona = this.add.zone(x, cy, ancho, alto).setInteractive({ useHandCursor: true });
      const hover = b.color === COLOR_BOTON_DESTACADO ? COLOR_BOTON_DESTACADO_HOVER : COLOR_BOTON_HOVER;
      zona.on('pointerover', () => dibujar(hover));
      zona.on('pointerout', () => dibujar(b.color));
      zona.on('pointerup', () => b.onClick());
      this.uiContainer.add(zona);
    });
  }

  // --- Botonera de acciones -------------------------------------------

  private renderBotoneraAcciones(): void {
    const { width, height } = this.scale;
    const areaTop = height * 0.58;
    const areaBottom = height - 110; // deja sitio al botón Volver
    const gap = 10;
    const ancho = width - MARGEN * 2;
    const altoDisponible = areaBottom - areaTop;
    const botonAlto = (altoDisponible - gap * (BARRIO_ACTIONS.length - 1)) / BARRIO_ACTIONS.length;

    BARRIO_ACTIONS.forEach((accion, i) => {
      const y = areaTop + i * (botonAlto + gap) + botonAlto / 2;
      this.crearBotonAccion(accion, width / 2, y, ancho, botonAlto);
    });
  }

  private crearBotonAccion(
    accion: (typeof BARRIO_ACTIONS)[number],
    x: number,
    y: number,
    ancho: number,
    alto: number,
  ): void {
    // El botón "Trabajar" se atenúa si ya agotaste el contador de la visita.
    const trabajandoAgotado =
      accion.id === 'trabajar' && this.trabajosEnVisita >= TRABAJAR_MAX_POR_VISITA;

    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(trabajandoAgotado ? 0x444450 : COLOR_BOTON);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    // Emoji a la izquierda.
    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 32, y, accion.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '26px',
        })
        .setOrigin(0, 0.5),
    );

    // Nombre en negrita + descripción debajo.
    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 76, y - 9, accion.nombre, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: trabajandoAgotado ? COLOR_TEXTO_DIM : COLOR_TEXTO,
        })
        .setOrigin(0, 0.5),
    );
    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 76, y + 11, accion.descripcion, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: COLOR_TEXTO_DIM,
          wordWrap: { width: ancho - 100 },
        })
        .setOrigin(0, 0.5),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => !trabajandoAgotado && dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(trabajandoAgotado ? 0x444450 : COLOR_BOTON));
    zona.on('pointerup', () => this.manejarAccion(accion));
    this.uiContainer.add(zona);
  }

  private manejarAccion(accion: (typeof BARRIO_ACTIONS)[number]): void {
    // Bloqueamos botones si estamos en mitad del evento cigarro.
    if (this.enEventoCigarro) return;

    switch (accion.id) {
      case 'trabajar':
        this.manejarTrabajar();
        break;
      case 'paki':
        this.manejarPaki();
        break;
      case 'axeliki':
        this.manejarAxelikiInicial();
        break;
      case 'dormir':
        this.manejarDormir();
        break;
    }
  }

  private manejarTrabajar(): void {
    if (this.trabajosEnVisita >= TRABAJAR_MAX_POR_VISITA) {
      // Ya has currado bastante hoy — solo frase neutra sin recompensa.
      this.panelMode = {
        tipo: 'frase',
        titulo: 'Trabajar',
        emoji: '🛠️',
        texto: FRASE_TRABAJAR_LIMITE,
        variante: 'neutro',
      };
      this.renderUI();
      // No dispara evento cigarro: no hiciste nada nuevo.
      return;
    }

    GameState.addDinero(TRABAJAR_GANANCIA);
    this.trabajosEnVisita += 1;

    const frase = pickDe(FRASES_TRABAJAR);
    const restantes = TRABAJAR_MAX_POR_VISITA - this.trabajosEnVisita;
    const sufijo =
      restantes > 0
        ? `\n\nTe quedan ${restantes} ${restantes === 1 ? 'turno' : 'turnos'} de curro hoy.`
        : '\n\nEsta ha sido la última del día.';

    this.panelMode = {
      tipo: 'frase',
      titulo: 'Trabajar',
      emoji: '🛠️',
      texto: `${frase}\n+${TRABAJAR_GANANCIA}€.${sufijo}`,
      variante: 'ok',
    };
    this.renderUI();
    this.intentarDispararEventoCigarro();
  }

  private manejarPaki(): void {
    const dinero = GameState.dinero;
    if (dinero < COMIDA_PRECIO) {
      const frase = pickDe(FRASES_PAKIO_SIN_DINERO);
      this.panelMode = {
        tipo: 'frase',
        titulo: 'Paki',
        emoji: '🏪',
        texto: `${frase}\nTe faltan ${COMIDA_PRECIO - dinero}€.`,
        variante: 'neutro',
      };
      this.renderUI();
      // No sumas comida ni pierdes dinero, pero el evento cigarro igual puede salir.
      this.intentarDispararEventoCigarro();
      return;
    }

    GameState.gastarDinero(COMIDA_PRECIO);
    GameState.addComida(COMIDA_CANTIDAD);
    const linea = pickDe(DIALOGOS_PAKI);
    const resultado = pickDe(FRASES_PAKI_OK);

    this.panelMode = {
      tipo: 'paki',
      lineaPaki: linea.texto,
      texto: `-${COMIDA_PRECIO}€. +${COMIDA_CANTIDAD} comida. ${resultado}`,
      ok: true,
    };
    this.renderUI();
    this.intentarDispararEventoCigarro();
  }

  private manejarDormir(): void {
    GameState.addSalud(DORMIR_SALUD);
    const frase = pickDe(FRASES_DORMIR);
    this.panelMode = {
      tipo: 'frase',
      titulo: 'Dormir',
      emoji: '🛏️',
      texto: `${frase}\nSalud a tope.`,
      variante: 'ok',
    };
    this.renderUI();
    // Dormir no dispara evento cigarro (es la salida lógica del barrio).
  }

  // --- AXELIKI: la mecánica de la espera ------------------------------

  private manejarAxelikiInicial(): void {
    // Reset estado + pintar la frase inicial. Decisiones del jugador
    // desde aquí son las que disparan la espera.
    const maxRondas = roll(0.5) ? 2 : 1;
    const llegadaEnRonda = roll(0.5) ? maxRondas : 1; // última ronda es "el momento justo"
    // Si por azar maxRondas=1 y llegadaEnRonda=1, todo bien; nunca quedan fuera de rango.
    this.axelikiState = {
      rondaActual: 0,
      maxRondas,
      llegadaEnRonda,
    };
    this.panelMode = { tipo: 'axeliki-inicial' };
    this.renderUI();
  }

  private axelikiEsperar(): void {
    const state = this.axelikiState;
    if (!state) return;
    state.rondaActual += 1;

    if (state.rondaActual === state.llegadaEnRonda) {
      // Ronda final: llega el BMW. Precio rebajado si es la última ronda (ronda==maxRondas).
      const esUltimaRonda = state.rondaActual >= state.maxRondas;
      const precio = esUltimaRonda ? YERBA_PRECIO_REBAJADO : YERBA_PRECIO;
      this.panelMode = { tipo: 'axeliki-compra', precio, rebajado: esUltimaRonda };
      this.renderUI();
      return;
    }

    // Ronda tonta: frase de espera, el jugador decide seguir o irse.
    const frase = pickDe(AXELIKI_FRASES_ESPERA);
    this.panelMode = { tipo: 'axeliki-espera', lineaAxeliki: frase };
    this.renderUI();
  }

  private axelikiInsistir(): void {
    const state = this.axelikiState;
    if (!state) return;

    // 50%: tarda una ronda más (ampliamos maxRondas para que vuelva a cuadrar).
    if (roll(0.5) && state.maxRondas < 3) {
      state.maxRondas += 1;
    }

    const frase = pickDe(AXELIKI_FRASES_INSISTIR);
    this.panelMode = { tipo: 'axeliki-espera', lineaAxeliki: frase };
    this.renderUI();
  }

  private axelikiIrse(): void {
    this.axelikiState = null;
    this.panelMode = {
      tipo: 'frase',
      titulo: 'AXELIKI',
      emoji: '🚗',
      texto: FRASE_AXELIKI_IRSE,
      variante: 'neutro',
    };
    this.renderUI();
    this.intentarDispararEventoCigarro();
  }

  private axelikiComprar(precio: number): void {
    const exito = GameState.gastarDinero(precio);
    if (!exito) {
      // No debería pasar porque el botón está deshabilitado sin saldo,
      // pero blindamos por si acaso.
      return;
    }
    GameState.addYerba(YERBA_CANTIDAD);
    this.axelikiState = null;
    this.panelMode = {
      tipo: 'frase',
      titulo: 'AXELIKI',
      emoji: '🚗',
      texto: `Yerba al saco. +${YERBA_CANTIDAD} por ${precio}€.\nEl BMW se va haciendo trompos.`,
      variante: 'ok',
    };
    this.renderUI();
    this.intentarDispararEventoCigarro();
  }

  private axelikiNoComprar(): void {
    this.axelikiState = null;
    this.panelMode = {
      tipo: 'frase',
      titulo: 'AXELIKI',
      emoji: '🚗',
      texto: 'Le dices que no hace falta. El AXELIKI se queda con el BMW al ralentí.',
      variante: 'neutro',
    };
    this.renderUI();
    this.intentarDispararEventoCigarro();
  }

  // --- Evento molesto: EL PROMO / EL AMIGO CIGARRO -------------------

  private intentarDispararEventoCigarro(): void {
    if (this.enEventoCigarro) return;
    if (!roll(EVENTO_CIGARRO_PROB)) return;

    const perseguidorId = perseguidorAleatorio();
    this.eventoPerseguidorId = perseguidorId;
    this.eventoFraseActual = this.frasePerseguidor(perseguidorId);
    this.eventoTeSigueMas = false;
    this.enEventoCigarro = true;

    this.renderEventoCigarro();
  }

  private frasePerseguidor(id: 'el-promo' | 'el-amigo-cigarro'): string {
    if (id === 'el-promo') return pickDe(PROMO_FRASES_PIDE).texto;
    return pickDe(AMIGO_CIGARRO_FRASES_PIDE).texto;
  }

  private renderEventoCigarro(): void {
    this.limpiarUI();

    const { width, height } = this.scale;
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 320;
    const cy = height * 0.32;

    const fondo = this.add.graphics();
    fondo.fillStyle(COLOR_PANEL_EVENTO, 0.97);
    fondo.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    fondo.lineStyle(2, 0x6e2c00, 1);
    fondo.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    fondo.setPosition(width / 2, cy);
    this.uiContainer.add(fondo);

    // Retrato del perseguidor.
    const retratoX = width / 2 - panelAncho / 2 + 70;
    const retratoY = cy - 30;
    const retrato = drawPortrait(this, retratoX, retratoY, this.eventoPerseguidorId, undefined, {
      mostrarApodo: false,
      radio: 38,
      tamanoEmoji: '38px',
    });
    this.uiContainer.add(retrato);

    const char = getCharacter(this.eventoPerseguidorId);
    const colorHex = '#' + (char.color ?? 0x4a4a55).toString(16).padStart(6, '0');

    this.uiContainer.add(
      this.add.text(width / 2 - panelAncho / 2 + 130, cy - 56, char.apodo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: colorHex,
      }),
    );

    // Globo de diálogo (rectángulo tipo cómic).
    const globoAncho = panelAncho - 150 - 30;
    const globoAlto = 100;
    const globo = this.add.graphics();
    globo.fillStyle(0xfaf3e0, 1);
    globo.fillRoundedRect(-globoAncho / 2, -globoAlto / 2, globoAncho, globoAlto, 14);
    globo.lineStyle(2, 0x22222e, 1);
    globo.strokeRoundedRect(-globoAncho / 2, -globoAlto / 2, globoAncho, globoAlto, 14);
    globo.setPosition(width / 2 + 30, cy - 30);
    this.uiContainer.add(globo);
    this.uiContainer.add(
      this.add
        .text(width / 2 + 30, cy - 30, this.eventoFraseActual, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '17px',
          color: '#22222e',
          align: 'center',
          wordWrap: { width: globoAncho - 20 },
        })
        .setOrigin(0.5),
    );

    // Botón DARLE UN CIGARRO (-1€ simbólico).
    const ancho = panelAncho - 60;
    const alto = 60;
    const y1 = cy + 70;
    const puedoPagar = GameState.dinero >= EVENTO_CIGARRO_COSTE;
    const colorBoton = puedoPagar ? COLOR_BOTON_CIGARRO : 0x555560;
    const boton = this.add.graphics();
    const dibujar = (c: number) => {
      boton.clear();
      boton.fillStyle(c, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(colorBoton);
    boton.setPosition(width / 2, y1);
    this.uiContainer.add(boton);
    this.uiContainer.add(
      this.add
        .text(
          width / 2,
          y1,
          puedoPagar
            ? `🚬 Darle un cigarro (−${EVENTO_CIGARRO_COSTE}€)`
            : `No llegas a ${EVENTO_CIGARRO_COSTE}€`,
          {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '18px',
            fontStyle: 'bold',
            color: COLOR_TEXTO,
          },
        )
        .setOrigin(0.5),
    );
    const zona = this.add
      .zone(width / 2, y1, ancho, alto)
      .setInteractive({ useHandCursor: puedoPagar });
    zona.on('pointerover', () => puedoPagar && dibujar(COLOR_BOTON_DESTACADO_HOVER));
    zona.on('pointerout', () => dibujar(colorBoton));
    zona.on('pointerup', () => this.eventoCigarroPagar());
    this.uiContainer.add(zona);

    // Botón ACELERAR EL PASO.
    const y2 = y1 + alto + 12;
    const boton2 = this.add.graphics();
    const dibujar2 = (c: number) => {
      boton2.clear();
      boton2.fillStyle(c, 1);
      boton2.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar2(COLOR_BOTON);
    boton2.setPosition(width / 2, y2);
    this.uiContainer.add(boton2);
    this.uiContainer.add(
      this.add
        .text(width / 2, y2, '🏃 Acelerar el paso', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );
    const zona2 = this.add.zone(width / 2, y2, ancho, alto).setInteractive({ useHandCursor: true });
    zona2.on('pointerover', () => dibujar2(COLOR_BOTON_HOVER));
    zona2.on('pointerout', () => dibujar2(COLOR_BOTON));
    zona2.on('pointerup', () => this.eventoCigarroEsquivar());
    this.uiContainer.add(zona2);
  }

  private eventoCigarroPagar(): void {
    const exito = GameState.gastarDinero(EVENTO_CIGARRO_COSTE);
    if (!exito) return;
    this.enEventoCigarro = false;

    this.panelMode = {
      tipo: 'frase',
      titulo: this.eventoPerseguidorId === 'el-promo' ? 'EL PROMO' : 'EL AMIGO CIGARRO',
      emoji: '🛴',
      texto: FRASE_DAR_CIGARRO,
      variante: 'neutro',
    };
    this.renderUI();
  }

  private eventoCigarroEsquivar(): void {
    // 40% de que "te siga una ronda más": mostramos frase de esquive + cola.
    this.eventoTeSigueMas = roll(0.4);
    this.enEventoCigarro = false;

    const fraseEsquive = pickDe(FRASES_ESQUIVE);
    const cola = this.eventoTeSigueMas ? `\n${pickDe(FRASES_SIGUE_MAS)}` : '';

    this.panelMode = {
      tipo: 'frase',
      titulo: this.eventoPerseguidorId === 'el-promo' ? 'EL PROMO' : 'EL AMIGO CIGARRO',
      emoji: '🛴',
      texto: `${fraseEsquive}${cola}`,
      variante: 'neutro',
    };
    this.renderUI();
    // Si te sigue una ronda más, otro evento puede dispararse al cerrar este panel.
    if (this.eventoTeSigueMas) {
      this.intentarDispararEventoCigarro();
    }
  }

  // --- Botón Volver al mapa (HUB) -------------------------------------

  private renderBotonVolver(): void {
    const { width, height } = this.scale;
    const ancho = 260;
    const alto = 60;
    const y = height - 50;

    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(width / 2, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(width / 2, y, '← Volver al mapa', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(width / 2, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerup', () => {
      this.scene.start('HubScene');
    });
    this.uiContainer.add(zona);
  }
}
